import {
	RFX_SCOPE_TABS,
	type RfxListScope,
	type RfxListTab,
} from "../types/rfx-list.type";

/**
 * URL path segment per scope — the value visible in the address bar and
 * consumed by 006's email deep links. This is DELIBERATELY distinct from the
 * `scope` API query value (`tenders_quotes`), which stays snake_case to match
 * the backend enum.
 */
export const RFX_SCOPE_ROUTE_SEGMENTS: Record<RfxListScope, string> = {
	tenders_quotes: "tenders-quotes",
	approvals: "approvals",
	panel: "panel",
};

export const RFX_ROUTE_SEGMENT_SCOPES: Record<string, RfxListScope> =
	Object.fromEntries(
		Object.entries(RFX_SCOPE_ROUTE_SEGMENTS).map(([scope, segment]) => [
			segment,
			scope as RfxListScope,
		])
	);

export const RFX_DEFAULT_TAB: RfxListTab = "current";

export const RFX_SCOPE_TITLES: Record<RfxListScope, string> = {
	tenders_quotes: "Procurer: Tenders & Quotes",
	approvals: "Approvals",
	panel: "Panel",
};

export const RFX_TAB_LABELS: Record<RfxListTab, string> = {
	current: "Current",
	evaluation: "Evaluation",
	awarded: "Awarded",
	drafts: "Drafts",
	archived: "Archived",
	past: "Past",
};

/**
 * Generic over the tab key so surfaces that are not backed by the backend
 * `RfxListTab` enum (the bidder list, whose tabs are submission states) can
 * describe their own tab bar without those keys leaking into `RfxListTab`.
 * Defaults to `RfxListTab`, so every existing use is unchanged.
 */
export interface RfxTabConfig<TTab extends string = RfxListTab> {
	label: string;
	tab: TTab;
}

/** Tab-config-as-data for a scope, in the order the tab bar renders them. */
export const getRfxTabsForScope = (scope: RfxListScope): RfxTabConfig[] =>
	RFX_SCOPE_TABS[scope].map((tab) => ({ tab, label: RFX_TAB_LABELS[tab] }));

export const isRfxListTabForScope = (
	scope: RfxListScope,
	tab: string | undefined
): tab is RfxListTab =>
	!!tab && (RFX_SCOPE_TABS[scope] as readonly string[]).includes(tab);

export const buildRfxListPath = (
	companyId: number | string,
	scope: RfxListScope,
	tab: RfxListTab = RFX_DEFAULT_TAB
): string =>
	`/account/${companyId}/rfx/${RFX_SCOPE_ROUTE_SEGMENTS[scope]}/${tab}`;
